import { BASE_COLOR_IDENTIFIER_SUFFIX } from '@/components/content/ProductDetails/constants';
import { USAGE_DEFINING } from '@/data/constants/catalog';
import { ColorClassification, ProductBaseColor } from '@/data/types/ColorType';

type Product = {
	manufacturer: string;
	attributes: any[];
};

export const getBaseColorDataByProduct = (
	colorClassification: ColorClassification | null,
	product: Product
): ProductBaseColor => {
	const { manufacturer, attributes } = product;

	// Guard against missing manufacturer or color data.
	if (!manufacturer || !colorClassification) {
		return { hasBaseColor: false };
	}

	// Find all potential components for a valid paint color.
	const brandColor =
		colorClassification.allBrands.find((brand) => manufacturer.includes(brand)) ?? null;

	const safeAttributes = Array.isArray(attributes) ? attributes : attributes ? [attributes] : [];
	const baseColorAttribute = safeAttributes.find(
		(attr) =>
			attr.identifier.includes(BASE_COLOR_IDENTIFIER_SUFFIX) && attr.usage === USAGE_DEFINING
	);
	const baseColor = baseColorAttribute?.values[0]?.value;

	const lineColor =
		colorClassification.allPaintLines.find((line) =>
			manufacturer.toLowerCase().includes(line.toLowerCase())
		) ?? null;

	// A product has a "base color" if all components are found and the base color is valid.
	const hasBaseColor = !!(
		brandColor &&
		baseColor &&
		lineColor &&
		colorClassification.allBaseColors.includes(baseColor)
	);

	return { brandColor, lineColor, baseColor, hasBaseColor };
};

export const AllowScrollWithTop = () => {
	const html = document.documentElement;
	html.style.position = '';
	html.style.overflow = '';
	html.style.margin = '';
	html.style.height = '';
	document.body.style.position = '';
	document.body.style.overflow = '';
	document.body.style.margin = '';
	document.body.style.height = '';
};

export const DisableScrollWithTop = () => {
	setTimeout(() => {
		const html = document.documentElement;
		html.style.position = 'relative';
		html.style.overflow = 'hidden';
		html.style.margin = '0';
		html.style.height = '100%';
		document.body.style.position = 'relative';
		document.body.style.overflow = 'hidden';
		document.body.style.margin = '0';
		document.body.style.height = '100%';
	}, 200);
};

export type AllPricesType = {
	displayPrice: string;
	displayUnidad: string;
	displayDecimal: string;
	offerPrice: string;
	offerUnidad: string;
	offerDecimal: string;
	listPrice: string;
	listUnidad: string;
	listDecimal: string;
	listMedidaPrice: string;
	listMedidaUnidad: string;
	listMedidaDecimal: string;
	offerMedidaPrice: string;
	offerMedidaUnidad: string;
	offerMedidaDecimal: string;
	minBundleUnit: string;
	minBundleDecimal: string;
	maxBundleUnit: string;
	maxBundleDecimal: string;
};

const COMMA_STRING = ',';
const REGEX = /\B(?=(\d{3})+(?!\d))/g;

/**
 * Formats a numeric value into its constituent parts for display.
 * @param value The numeric value to format.
 * @returns An object containing the full formatted string, the integer part, and the decimal part.
 */
const formatPriceValue = (value: number | string | undefined) => {
	const num = parseFloat(String(value ?? '0'));
	if (isNaN(num)) {
		return { full: '0.00', unit: '0', decimal: '00' };
	}
	const [unit, decimal] = num.toFixed(2).split('.');
	return {
		full: `${unit.replace(REGEX, COMMA_STRING)}.${decimal}`,
		unit: unit.replace(REGEX, COMMA_STRING),
		decimal,
	};
};

/**
 * Processes bundle pricing information from product components.
 * @param components An array of product components.
 * @param prices The price object to populate.
 */
const processBundlePrices = (components: any[], prices: AllPricesType) => {
	if (!components || components.length === 0) {
		return;
	}

	const bundlePrices = components
		.map((component) => {
			const offer = component.price?.find((p: any) => p.usage === 'Offer');
			const display = component.price?.find((p: any) => p.usage === 'Display');
			return parseFloat(offer?.value ?? display?.value ?? '0');
		})
		.filter((p) => p > 0);

	if (bundlePrices.length > 0) {
		const minBundlePrice = Math.min(...bundlePrices);
		const maxBundlePrice = bundlePrices.reduce((acc, curr) => acc + curr, 0);

		const { unit: minUnit, decimal: minDecimal } = formatPriceValue(minBundlePrice);
		prices.minBundleUnit = minUnit;
		prices.minBundleDecimal = minDecimal;

		const { unit: maxUnit, decimal: maxDecimal } = formatPriceValue(maxBundlePrice);
		prices.maxBundleUnit = maxUnit;
		prices.maxBundleDecimal = maxDecimal;

		const savings = components.reduce((acc: number, p: any) => {
			const offer = p.price.find((price: any) => price.usage === 'Offer');
			return acc + parseFloat(offer?.value ?? '0');
		}, 0);

		if (savings > 0) {
			prices.listPrice = formatPriceValue(savings).full;
		}
	}
};

/**
 * Formats product prices, including offer, display, and list prices,
 * and handles special cases for bundled or measured products.
 *
 * @param product The product data object.
 * @returns An object containing all formatted price values.
 */
export const numberWithCommas = (product: any): AllPricesType => {
	const prices: AllPricesType = {
		displayPrice: '0.00',
		displayUnidad: '0',
		displayDecimal: '00',
		offerPrice: '0.00',
		offerUnidad: '0',
		offerDecimal: '00',
		listPrice: '0.00',
		listUnidad: '0',
		listDecimal: '00',
		listMedidaPrice: '0.00',
		listMedidaUnidad: '0',
		listMedidaDecimal: '00',
		offerMedidaPrice: '0.00',
		offerMedidaUnidad: '0',
		offerMedidaDecimal: '00',
		minBundleUnit: '0',
		minBundleDecimal: '00',
		maxBundleUnit: '0',
		maxBundleDecimal: '00',
	};

	if (!product?.price) {
		return prices;
	}

	const offerPriceObj = product.price.find((p: any) => p.usage === 'Offer');
	const displayPriceObj = product.price.find((p: any) => p.usage === 'Display');

	processBundlePrices(product.components, prices);

	const field1 = offerPriceObj?.field1;

	// Measured pricing from field1
	if (field1) {
		const {
			full: listMedidaFull,
			unit: listMedidaUnit,
			decimal: listMedidaDecimal,
		} = formatPriceValue(field1.LMP);
		prices.listMedidaPrice = listMedidaFull;
		prices.listMedidaUnidad = listMedidaUnit;
		prices.listMedidaDecimal = listMedidaDecimal;

		const { full: listFull, unit: listUnit, decimal: listDecimal } = formatPriceValue(field1.LP);
		prices.listPrice = listFull;
		prices.listUnidad = listUnit;
		prices.listDecimal = listDecimal;

		const {
			full: offerMedidaFull,
			unit: offerMedidaUnit,
			decimal: offerMedidaDecimal,
		} = formatPriceValue(field1.OMP);
		prices.offerMedidaPrice = offerMedidaFull;
		prices.offerMedidaUnidad = offerMedidaUnit;
		prices.offerMedidaDecimal = offerMedidaDecimal;
	}

	// Standard display and offer prices
	const {
		full: displayFull,
		unit: displayUnit,
		decimal: displayDecimal,
	} = formatPriceValue(displayPriceObj?.value);
	prices.displayPrice = displayFull;
	prices.displayUnidad = displayUnit;
	prices.displayDecimal = displayDecimal;

	const {
		full: offerFull,
		unit: offerUnit,
		decimal: offerDecimal,
	} = formatPriceValue(offerPriceObj?.value);
	prices.offerPrice = offerFull;
	prices.offerUnidad = offerUnit;
	prices.offerDecimal = offerDecimal;

	return prices;
};

// Get month abbrivation
// Usage:
// getMonthAbbr(0); // "ene"
// getMonthAbbr(1); // "feb"
// getMonthAbbr(2); // "mar"
export const getMonthAbbr = (monthIndex: number, locale = 'es-ES' as string) => {
	const date = new Date(2000, monthIndex, 1); // arbitrary day
	return new Intl.DateTimeFormat(locale, { month: 'short' }).format(date);
};

/**
 * Add comas to the price number
 *
 * @param price The product price without format, example: 1000.
 * @returns The string value with a comma, example: 1,000.
 */
export const priceWithComas = (price: string) => {
	const result = parseFloat(price).toFixed(2).toString().replace(REGEX, COMMA_STRING);
	return result;
};
